import type { ComponentType } from 'react';
import Analytics from './modules/analytics';
import Audit from './modules/audit';
import Billing from './modules/billing';
import Bridge from './modules/bridge';
import Comms from './modules/comms';
import Equipment from './modules/equipment';
import Feedback from './modules/feedback';
import Identity from './modules/identity';
import Loyalty from './modules/loyalty';
import Lpr from './modules/lpr';
import Ops from './modules/ops';
import Payments from './modules/payments';
import Pos from './modules/pos';
import Sites from './modules/sites';
import Staff from './modules/staff';
import type { ModuleViewProps } from './shell/types';

export interface ModuleRoute {
  readonly key: string;
  readonly spec: number;
  readonly titleRu: string;
  readonly titleKk: string;
  /**
   * Действия из матрицы прав ядра (moika-core/src/auth/roles.ts). Раздел виден,
   * если разрешено хотя бы одно из них. Список сверяется с сервером, а не
   * придумывается интерфейсом: раньше меню показывало все одиннадцать разделов
   * всем ролям, и кассир упирался в 403 после клика.
   */
  readonly requires: readonly string[];
  readonly Component: ComponentType<ModuleViewProps>;
}

export const MODULE_ROUTES: readonly ModuleRoute[] = [
  {
    key: 'identity',
    spec: 1,
    titleRu: 'Идентификация',
    titleKk: 'Сәйкестендіру',
    requires: ['customer.view'],
    Component: Identity,
  },
  {
    key: 'lpr',
    spec: 2,
    titleRu: 'Распознавание номеров',
    titleKk: 'Нөмірлерді тану',
    requires: ['report.site', 'admin.manage'],
    Component: Lpr,
  },
  {
    key: 'loyalty',
    spec: 4,
    titleRu: 'Лояльность',
    titleKk: 'Адалдық',
    requires: ['loyalty.adjust', 'loyalty.rule.manage'],
    Component: Loyalty,
  },
  {
    key: 'billing',
    spec: 5,
    titleRu: 'Подписка',
    titleKk: 'Жазылым',
    requires: ['subscription.manage'],
    Component: Billing,
  },
  {
    key: 'feedback',
    spec: 6,
    titleRu: 'Обращения',
    titleKk: 'Өтініштер',
    requires: ['feedback.view'],
    Component: Feedback,
  },
  {
    key: 'pos',
    spec: 7,
    titleRu: 'Касса и смены',
    titleKk: 'Касса және ауысымдар',
    requires: ['shift.open', 'shift.close', 'catalog.manage'],
    Component: Pos,
  },
  {
    key: 'analytics',
    spec: 8,
    titleRu: 'Аналитика',
    titleKk: 'Аналитика',
    requires: ['report.site', 'report.network'],
    Component: Analytics,
  },
  {
    key: 'payments',
    spec: 9,
    titleRu: 'Платежи',
    titleKk: 'Төлемдер',
    requires: ['report.site', 'admin.manage'],
    Component: Payments,
  },
  {
    key: 'comms',
    spec: 10,
    titleRu: 'Коммуникации',
    titleKk: 'Коммуникациялар',
    requires: ['comms.send'],
    Component: Comms,
  },
  {
    key: 'bridge',
    spec: 11,
    titleRu: 'Обмен и миграция',
    titleKk: 'Алмасу және көші-қон',
    requires: ['report.network'],
    Component: Bridge,
  },
  {
    /*
     * У раздела нет требований намеренно, и `visibleRoutes` трактует пустой
     * список как «виден всем». Вкладка «Мой профиль» обязана быть доступна
     * кассиру, у которого прав на чужие учётные записи нет; вкладку «Список»
     * гейтит сам раздел по `employee.view`.
     */
    key: 'staff',
    spec: 0,
    titleRu: 'Сотрудники',
    titleKk: 'Қызметкерлер',
    requires: [],
    Component: Staff,
  },
  {
    /*
     * Журнал стоит рядом с эксплуатацией и требует того же права `audit.view`:
     * читают его те же трое — управляющий по своему объекту, администратор сети
     * и аудитор.
     */
    key: 'audit',
    spec: 0,
    titleRu: 'Журнал',
    titleKk: 'Журнал',
    requires: ['audit.view'],
    Component: Audit,
  },
  {
    key: 'ops',
    spec: 12,
    titleRu: 'Эксплуатация',
    titleKk: 'Пайдалану',
    requires: ['audit.view'],
    Component: Ops,
  },
  {
    /*
     * Оборудование мойки (ТЗ §9.5, М6.8): управляющий смотрит состояние и
     * прогноз отказов, кассир и оператор запускают программу.
     */
    key: 'equipment',
    spec: 12,
    titleRu: 'Оборудование',
    titleKk: 'Жабдық',
    requires: ['audit.view', 'order.create'],
    Component: Equipment,
  },
  {
    /*
     * Объекты — настройка сети, а не объекта, поэтому право сетевое
     * (`admin.manage` входит в NETWORK_ONLY). Раздел стоит последним рядом с
     * эксплуатацией: заводят объект раз в несколько месяцев, а не ежедневно.
     */
    key: 'sites',
    spec: 0,
    titleRu: 'Объекты',
    titleKk: 'Нысандар',
    requires: ['admin.manage'],
    Component: Sites,
  },
];

export function visibleRoutes(permissions: readonly string[]): readonly ModuleRoute[] {
  const granted = new Set(permissions);
  return MODULE_ROUTES.filter(
    // Пустой список требований означает «виден всем»: так устроен раздел со
    // своим профилем, где менять собственный пароль обязан каждый.
    (route) => route.requires.length === 0 || route.requires.some((action) => granted.has(action)),
  );
}
